/**
 * The step log of a training run (spec 53 sections 4.3 and 4.4): what the
 * person did at each step, as the grader reads it.
 *
 *   [{ id, mode: 'self'|'shown'|'read'|'skipped'|null, refusals, title }]
 *
 * One entry per step id. A step met again (Back, then the step once more)
 * keeps its best mode, so redoing an Enter step the fallback filled makes it
 * the person's own, and backing over a step they already did never takes it
 * away. Refusals (a non-2xx answer) are counted and never fail anything; a
 * refused step is still open, with `mode: null` until it is done.
 *
 * Kept in memory by TourHost and in sessionStorage under `lw:training`, so a
 * refresh mid-module keeps it. `lw:training` is shared: this file owns its
 * `run` key and leaves every other key (the practice page's unsent receipts,
 * spec 53 5.3 step 5) as it found it. Storage can be unavailable; then the
 * log lives for this page only.
 */
const KEY = 'lw:training';
const MODES = ['self', 'shown', 'read', 'skipped'];
const RANK = { self: 4, shown: 3, read: 2, skipped: 1 };
const MAX_STEPS = 200;

function rank(mode) {
  return RANK[mode] || 0;
}

function clean(entry) {
  return {
    id: entry.id,
    mode: MODES.includes(entry.mode) ? entry.mode : null,
    refusals: Number.isInteger(entry.refusals) && entry.refusals > 0 ? entry.refusals : 0,
    title: typeof entry.title === 'string' ? entry.title.slice(0, 80) : '',
  };
}

/**
 * What a run's steps captured (spec 33 `capture`), kept to plain ids:
 * string or finite-number values under word keys, nothing nested.
 */
function cleanCaptured(captured) {
  const out = {};
  if (!captured || typeof captured !== 'object' || Array.isArray(captured)) return out;
  for (const [key, value] of Object.entries(captured)) {
    if (!/^[A-Za-z][A-Za-z0-9_]{0,40}$/.test(key)) continue;
    if (typeof value === 'string' && value.length <= 200) out[key] = value;
    else if (typeof value === 'number' && Number.isFinite(value)) out[key] = value;
  }
  return out;
}

function upsert(log, id, change) {
  const out = Array.isArray(log) ? log.slice() : [];
  const at = out.findIndex(e => e.id === id);
  const current = at === -1 ? { id, mode: null, refusals: 0, title: '' } : out[at];
  const next = change({ ...current });
  if (at === -1) out.push(next);
  else out[at] = next;
  return out.slice(0, MAX_STEPS);
}

/** The step finished, or was skipped or read: keep the best mode it has had. */
export function record(log, { id, mode, title = '' }) {
  if (typeof id !== 'string' || !MODES.includes(mode)) return Array.isArray(log) ? log : [];
  return upsert(log, id, entry => ({
    ...entry,
    mode: rank(mode) > rank(entry.mode) ? mode : entry.mode,
    title: entry.title || String(title || '').slice(0, 80),
  }));
}

/** The server said no to this step. Logged, never counted against it. */
export function refuse(log, { id, title = '' }) {
  if (typeof id !== 'string') return Array.isArray(log) ? log : [];
  return upsert(log, id, entry => ({
    ...entry,
    refusals: entry.refusals + 1,
    title: entry.title || String(title || '').slice(0, 80),
  }));
}

function readAll() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    const saved = raw ? JSON.parse(raw) : null;
    return saved && typeof saved === 'object' && !Array.isArray(saved) ? saved : {};
  } catch (e) {
    return {};
  }
}

function writeAll(all) {
  try {
    if (Object.keys(all).length) window.sessionStorage.setItem(KEY, JSON.stringify(all));
    else window.sessionStorage.removeItem(KEY);
  } catch (e) {
    // The log lives in memory for this page; a refresh starts it over.
  }
}

/**
 * The run this tab is in the middle of, or null:
 * { tourId, module, version, startedAt, steps, captured, finished, mode,
 *   handover, left }.
 *
 * `mode` is 'free' for the task and its checklist (TaskHost, spec 53 15) and
 * 'guided' for the cards (TourHost). `handover` marks a free run the person
 * moved to the cards ("Guide me step by step"), whose log the cards keep;
 * `left` one they left, which a reload doesn't start again. `context` is what
 * the checklist resolved (the suggested pickup, the driver), so the cards it
 * hands over to suggest the same, plain values only.
 */
export function readRun() {
  const run = readAll().run;
  if (!run || typeof run.tourId !== 'string' || !Array.isArray(run.steps)) return null;
  return {
    tourId: run.tourId,
    module: typeof run.module === 'string' ? run.module : run.tourId,
    version: Number.isInteger(run.version) ? run.version : null,
    startedAt: typeof run.startedAt === 'string' ? run.startedAt : null,
    steps: run.steps.filter(e => e && typeof e.id === 'string').map(clean),
    captured: cleanCaptured(run.captured),
    finished: Boolean(run.finished),
    mode: run.mode === 'free' ? 'free' : 'guided',
    handover: run.handover === true,
    left: run.left === true,
    context: cleanCaptured(run.context),
  };
}

export function writeRun(run) {
  const all = readAll();
  all.run = {
    tourId: run.tourId,
    module: run.module,
    version: run.version,
    startedAt: run.startedAt,
    steps: (run.steps || []).map(clean),
    captured: cleanCaptured(run.captured),
    finished: Boolean(run.finished),
    mode: run.mode === 'free' ? 'free' : 'guided',
    handover: run.handover === true,
    left: run.left === true,
    context: cleanCaptured(run.context),
  };
  writeAll(all);
}

/** Forget the run (the practice page has posted it). Other keys stay. */
export function clearRun() {
  const all = readAll();
  delete all.run;
  writeAll(all);
}
